import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { setStrategy, type PricingStrategy } from '@/api/pricing'
import {
  createRollout, getRolloutFamily, hebeAusnahmeAuf, setAusnahme, setRolloutStatus,
  type FamilyRolloutInfo, type FamilyStart, type FamilyVariante, type RegelInfo, type SpurAusnahme,
} from '@/api/priceRollout'
import { fmtEUR } from '@/lib/pricing-format'
import { ApiError } from '@/api/client'
import { Panel } from '@/components/Panel'
import { Button } from '@/components/ui/button'
import { ArtikelKennung } from '@/components/rollout/RolloutAnzeige'
import { RolloutVerlauf, useRolloutVerlauf } from '@/components/rollout/RolloutVerlauf'
import { cn } from '@/lib/utils'
import { AngebotsLinkZeile, AngebotsLinks, PaarKnopf, PortalZelle, RolloutKnopf } from './PortalZelle'
import { FortsetzenDialog, StartDialog, StrategieDialog } from './RolloutDialoge'
import { SammelstartDialog, type SammelAuswahl } from './SammelstartDialog'
import { SAMMEL_MAX, sammelAuswaehlbar, startStand } from './sammelstart'
import { nachStrategieAenderung, useStrategieGeaendertAb } from './strategieStand'
import {
  PORTAL_FARBE, SICHT_PORTALE, ausnahmeMoeglich, ansichtAusUrl, ansichtInUrl, fehlerText, filterUndSortiere, grundText, gruppiereNachAngebot, kopplungsPaar, portalName,
  sammelKnopfText, startAmZiel, startMeldung, strategieZiel,
  type RolloutAnsicht, type RolloutFilter, type SichtPortal, type Sortierung, type StatusFilter, type StrategieFilter,
} from './rolloutView'

interface Props { sku: string }

const fmtStand = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : 'keine Ernte'

const fehlerVon = (e: unknown, fallback: string) => (e instanceof ApiError ? fehlerText(e.message) : fallback)

/**
 * Preis-Rollout im Grundartikel (Plan 2026-10-19 §3 S2): echte Aktivität je Variante und Portal,
 * eine Strategie je Variante, gezielter Start je Variante mit Bestätigung je Scope.
 * Kein neuer Schreibweg: Start = POST /pricing/rollout/rollouts/, Pause/Fortsetzen = PATCH.
 * Sammelstart Basis (Plan 2026-10-27-rollout-sammelstart): angehakte Varianten mit einer Bestätigung,
 * aber Einzelstarts nacheinander mit Reload je Variante; höchstens SAMMEL_MAX je Serie.
 */
export function PreisRolloutBlock({ sku }: Props) {
  const queryClient = useQueryClient()
  const location = useLocation()
  const [params, setParams] = useSearchParams()
  const ansicht = useMemo(() => ansichtAusUrl(params), [params])
  const setAnsicht = (next: Partial<RolloutAnsicht>) =>
    setParams(ansichtInUrl(params, { ...ansicht, ...next }), { replace: true, state: location.state })

  const { data: fam, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['rollout-family', sku],
    queryFn: () => getRolloutFamily(sku),
    staleTime: 15_000,
  })
  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ['rollout-family', sku] })
    void queryClient.invalidateQueries({ queryKey: ['rollout-status'] })
    void queryClient.invalidateQueries({ queryKey: ['rollout-verlauf'] })
  }

  const sichtbar = useMemo(() => (fam ? filterUndSortiere(fam.varianten, ansicht) : []), [fam, ansicht])
  // Sortierung „Angebot“: Varianten derselben Auktion (eBay) bzw. desselben Angebots stehen zusammen.
  const gruppen = useMemo(
    () => (ansicht.sort === 'angebot' ? gruppiereNachAngebot(sichtbar, ansicht.portale) : sichtbar.map((v) => ({ links: [], varianten: [v], parents: [] }))),
    [sichtbar, ansicht.sort, ansicht.portale],
  )
  const gruppenPortal: SichtPortal = ansicht.portale.includes('ebay') ? 'ebay' : ansicht.portale[0] ?? 'ebay'
  // Strategie folgt dem Portalfilter: eBay/Shopify/Metro hängen an der Basis, Amazon hat eine eigene.
  const stratZiel = strategieZiel(ansicht.portale)
  // S4: ein Abruf für alle Rollouts der Familie (Paar Basis + eBay = eine Zeile).
  const verlaufIds = useMemo(() => (fam ? fam.varianten.flatMap((v) => verlaufZeilen(v).map((z) => z.id)) : []), [fam])
  const verlauf = useRolloutVerlauf(verlaufIds)

  // ── Start ──
  // Der Dialog zeigt immer die aktuellen Familiendaten. `bestaetigt` ist der Stand beim Öffnen:
  // ändert ein Refetch (z. B. nach Strategiewechsel) Ziel, Scopes oder Startbarkeit, ist der Start
  // gesperrt, bis der neue Stand ausdrücklich übernommen wurde — gesendet wird nie ein alter Schnappschuss.
  const [startWahl, setStartWahl] = useState<{ sku: string; art: 'amazon' | 'base'; bestaetigt: string } | null>(null)
  const [startFehler, setStartFehler] = useState<string | null>(null)
  const startLaeuft = useRef(false)
  const startV = startWahl ? fam?.varianten.find((v) => v.sku === startWahl.sku) : undefined
  const startAktuell = startV && startWahl ? { v: startV, start: startV.start[startWahl.art] } : null
  const startVeraltet = startWahl !== null && (!startAktuell || startStand(startAktuell.start) !== startWahl.bestaetigt)
  const starten = useMutation({
    mutationFn: ({ v, start }: { v: FamilyVariante; start: FamilyStart }) => createRollout(v.sku, start.portal, start.ziel!),
    onSuccess: (res) => {
      // S2.1: Anlegen und sofortiger erster Schritt in einer Rückmeldung.
      const m = startMeldung(res)
      if (m.art === 'warnung') toast.warning(m.text)
      else toast.success(m.text)
      setStartWahl(null)
      neuLaden()
    },
    onError: (e) => setStartFehler(fehlerVon(e, 'Rollout-Start fehlgeschlagen')),
    onSettled: () => { startLaeuft.current = false },
  })
  const oeffneStart = (v: FamilyVariante, art: 'amazon' | 'base') => {
    setStartFehler(null)
    setStartWahl({ sku: v.sku, art, bestaetigt: startStand(v.start[art]) })
  }
  const bestaetigeStart = () => {
    if (!startAktuell || startVeraltet || startSperre !== null || startLaeuft.current) return
    const { start } = startAktuell
    if (!start.startbar || start.ziel === null) return
    startLaeuft.current = true
    setStartFehler(null)
    starten.mutate(startAktuell)
  }

  // ── Strategie ──
  const [stratOffen, setStratOffen] = useState<{ v: FamilyVariante; neu: PricingStrategy; ziel: 'amazon' | 'base' | 'both' } | null>(null)
  const strategie = useMutation({
    mutationFn: ({ v, neu, ziel }: { v: FamilyVariante; neu: PricingStrategy; ziel: 'amazon' | 'base' | 'both' }) => setStrategy(v.sku, neu, ziel),
    onSuccess: () => {
      toast.success('Strategie gespeichert')
      setStratOffen(null)
      nachStrategieAenderung(queryClient)
    },
    onError: (e) => toast.error(fehlerVon(e, 'Strategie nicht gespeichert')),
  })

  // Nach einem Strategiewechsel (hier, per Angebots-Batch oder Rückgängig im Reiter „Angebote“) gelten Ziele
  // erst nach erfolgreichem Neuladen; scheitert es, bleibt der Start gesperrt (sonst wäre das alte Ziel aus
  // dem Cache wieder startbar). Ebenso bei jedem Ladefehler.
  const frischAb = useStrategieGeaendertAb()
  const datenAlt = frischAb !== null && dataUpdatedAt < frischAb
  const startSperre: string | null =
    strategie.isPending || isFetching ? 'Daten werden aktualisiert – Start kurz gesperrt.'
    : isError || datenAlt ? 'Daten nach Änderung nicht neu geladen – Start gesperrt. Bitte Seite neu laden.'
    : null

  // ── Sammelstart (nur Basis) ──
  const [auswahl, setAuswahl] = useState<Set<string>>(() => new Set())
  const [sammel, setSammel] = useState<SammelAuswahl[] | null>(null)
  const sammelGrund = (v: FamilyVariante) => sammelAuswaehlbar(v, fam?.kill_aktiv ?? true, startSperre)
  const auswaehlbar = sichtbar.filter((v) => sammelGrund(v) === null)
  const angehakt = auswaehlbar.filter((v) => auswahl.has(v.sku))
  const alleAngehakt = auswaehlbar.length > 0 && angehakt.length === auswaehlbar.length
  const umschalten = (sku: string) => setAuswahl((alt) => {
    const neu = new Set(alt)
    if (neu.has(sku)) neu.delete(sku)
    else neu.add(sku)
    return neu
  })
  const alleUmschalten = () => setAuswahl(alleAngehakt ? new Set() : new Set(auswaehlbar.map((v) => v.sku)))
  const oeffneSammel = () => {
    if (angehakt.length === 0 || angehakt.length > SAMMEL_MAX) return
    setSammel(angehakt.map((v) => ({ sku: v.sku, v, bestaetigt: startStand(v.start.base) })))
  }
  const ladeFamilieFrisch = () => queryClient.fetchQuery({ queryKey: ['rollout-family', sku], queryFn: () => getRolloutFamily(sku), staleTime: 0 })

  // ── Pause / Fortsetzen ──
  const [fortOffen, setFortOffen] = useState<{ sku: string; portal: string; id: number; ziel: number; regel: RegelInfo | null } | null>(null)
  const [fortFehler, setFortFehler] = useState<string | null>(null)
  const pausieren = useMutation({
    mutationFn: (id: number) => setRolloutStatus(id, 'paused'),
    onSuccess: () => { toast.success('Rollout pausiert'); neuLaden() },
    onError: (e) => toast.error(fehlerVon(e, 'Pausieren fehlgeschlagen')),
  })
  const fortsetzen = useMutation({
    mutationFn: (id: number) => setRolloutStatus(id, 'active'),
    onSuccess: () => { toast.success('Rollout fortgesetzt'); setFortOffen(null); neuLaden() },
    onError: (e) => setFortFehler(fehlerVon(e, 'Fortsetzen fehlgeschlagen')),
  })
  const oeffneFortsetzen = (v: FamilyVariante, portal: string, r: Pick<FamilyRolloutInfo, 'id' | 'ziel'>, regel: RegelInfo | null) => {
    setFortFehler(null)
    setFortOffen({ sku: v.sku, portal, id: r.id, ziel: r.ziel, regel })
  }
  const busy = pausieren.isPending || fortsetzen.isPending

  // ── Ausnahme „Ist-Preis halten“ (Plan 2026-10-30): Ziel und Ist liest der Server selbst;
  // laufende Rollouts der Spur pausieren dabei. ──
  const ausnahme = useMutation({
    mutationFn: async ({ v, spur, an }: { v: FamilyVariante; spur: 'amazon' | 'base'; an: boolean }) => {
      if (an) return { an, r: await setAusnahme(v.sku, spur) }
      await hebeAusnahmeAuf(v.sku, spur)
      return { an, r: null }
    },
    onSuccess: ({ an, r }) => {
      if (an && r) {
        const pause = r.pausiert === 0 ? '' : r.pausiert === 1 ? ' – laufender Rollout pausiert' : ` – ${r.pausiert} Rollouts pausiert`
        toast.success(`Ist-Preis ${fmtEUR(r.preis)} gehalten${pause}`)
      } else toast.success('Ausnahme aufgehoben – Start wieder möglich')
      neuLaden()
    },
    onError: (e) => toast.error(fehlerVon(e, 'Ausnahme nicht gespeichert')),
  })
  const halteZeile = (v: FamilyVariante, spur: 'amazon' | 'base') => (
    <IstHalten
      sku={v.sku} spur={spur} ausnahme={v.spuren[spur].ausnahme ?? null} istPreis={v.spuren[spur].ist_preis}
      moeglich={ausnahmeMoeglich(v, spur)}
      disabled={ausnahme.isPending || startSperre !== null || (fam?.kill_aktiv ?? true)}
      onWechsel={(an) => { if (!ausnahme.isPending) ausnahme.mutate({ v, spur, an }) }}
    />
  )

  const ernte = fam
    ? SICHT_PORTALE.map((p) => `${portalName(p)} ${fmtStand(fam.ernte[p])}`).join(' · ')
    : null

  return (
    <Panel
      title="Preis-Rollout"
      subtitle={`Echte Angebote je Portal${ernte ? ` (Stand ${ernte})` : ''}. „Rollout starten“ startet genau einen Rollout; für mehrere Varianten Häkchen setzen und „Basis (+ eBay) für N starten“.`}
      actions={<Link to="/preise/rollout" className="text-xs text-primary hover:underline whitespace-nowrap">Regeln &amp; alle Rollouts →</Link>}
      bodyClassName="p-0"
    >
      <Werkzeugleiste
        ansicht={ansicht}
        setAnsicht={setAnsicht}
        zaehler={fam ? `${sichtbar.length} von ${fam.varianten.length} Varianten` : null}
      />

      {fam?.kill_aktiv && (
        <div className="border-b border-line-2 border-l-4 border-l-coral bg-coral-soft px-5 py-2 text-sm font-semibold text-ink">
          Not-Aus aktiv – kein Rollout-Start möglich. Aufheben nur auf der Seite Preis-Rollout.
        </div>
      )}
      {fam && !fam.live_writes && (
        <div className="border-b border-line-2 bg-panel-2 px-5 py-2 text-xs text-ink-2">
          Globale LIVE-Freigabe aus: alle Rollouts schreiben derzeit nur ins Protokoll, kein Preis wird geändert.
        </div>
      )}

      {fam && fam.varianten.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-b border-line-2 px-5 py-2">
          <Button
            size="sm" variant="outline" className="h-7"
            disabled={angehakt.length === 0 || angehakt.length > SAMMEL_MAX || startSperre !== null || fam.kill_aktiv}
            onClick={oeffneSammel}
          >
            {sammelKnopfText(angehakt)}
          </Button>
          {angehakt.length > SAMMEL_MAX && (
            <span className="text-xs text-ink-2">{`höchstens ${SAMMEL_MAX} Varianten je Serie – bitte Auswahl verkleinern`}</span>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="py-10 text-center text-ink-2">Lade Angebote und Rollouts…</div>
      ) : isError ? (
        <div className="py-10 text-center text-sm text-ink-2">
          Rollout-Daten nicht geladen: {error instanceof Error ? error.message : 'unbekannter Fehler'}.{' '}
          <button type="button" className="text-primary hover:underline" onClick={() => void refetch()}>Erneut laden</button>
        </div>
      ) : !fam || fam.varianten.length === 0 ? (
        <div className="py-10 text-center text-ink-2">Dieser Artikel hat keine Varianten.</div>
      ) : sichtbar.length === 0 ? (
        <div className="py-10 text-center text-sm text-ink-2">
          Keine Variante passt zu dieser Ansicht.{' '}
          <Button size="sm" variant="outline" className="ml-2 h-7" onClick={() => setAnsicht({ status: 'alle', strategie: 'alle', rollout: 'alle' })}>
            Alle zeigen
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line-2 text-left text-[10px] uppercase tracking-wider text-muted">
                <th className="px-5 py-2 font-semibold">
                  <span className="inline-flex items-center gap-2">
                    <input
                      type="checkbox" aria-label="Alle auswählbaren Varianten auswählen"
                      className="accent-primary focus-visible:outline-2 focus-visible:outline-primary"
                      checked={alleAngehakt} disabled={auswaehlbar.length === 0} onChange={alleUmschalten}
                    />
                    Variante
                  </span>
                </th>
                <th className="px-3 py-2 font-semibold">Strategie</th>
                {ansicht.portale.map((p) => (
                  <th key={p} className="px-3 py-2 font-semibold">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block h-2.5 w-[3px] rounded-sm" style={{ background: PORTAL_FARBE[p] }} aria-hidden />
                      {portalName(p)}
                      <span className="font-normal normal-case tracking-normal">· Ist → Ziel</span>
                    </span>
                  </th>
                ))}
                <th className="px-3 py-2 pr-5 font-semibold">Start</th>
              </tr>
            </thead>
            {gruppen.map((g) => (
              <Fragment key={g.varianten[0]!.sku}>
              {g.varianten.length > 1 && (
                <tbody>
                  <tr data-testid="angebots-gruppe" className="border-b border-line-2 bg-panel-2/60" style={{ boxShadow: `inset 3px 0 0 ${PORTAL_FARBE[gruppenPortal]}` }}>
                    <td colSpan={3 + ansicht.portale.length} className="px-5 py-1.5">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-2">
                        <span className="font-semibold uppercase tracking-wider text-muted">
                          {g.parents.length > 0 ? 'Gleiche Amazon-Familie' : 'Gleiches Angebot'}
                        </span>
                        {g.parents.length > 0 && (
                          <span>Parent <span className="font-mono text-ink">{g.parents.join(', ')}</span></span>
                        )}
                        {g.links.map((l) => <AngebotsLinkZeile key={l.url} l={l} />)}
                        <span>{g.varianten.length} Varianten</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              )}
              {g.varianten.map((v) => (
              <tbody key={v.sku} className="border-b border-line-2 last:border-b-0 hover:bg-panel-2/40">
                <tr className="align-top" style={g.varianten.length > 1 ? { boxShadow: `inset 3px 0 0 ${PORTAL_FARBE[gruppenPortal]}` } : undefined}>
                  <td className="px-5 py-3">
                    <div className="flex items-start gap-2">
                    <input
                      type="checkbox" aria-label={`Variante ${v.sku} für Sammelstart auswählen`}
                      className="mt-1 accent-primary focus-visible:outline-2 focus-visible:outline-primary"
                      checked={auswahl.has(v.sku) && sammelGrund(v) === null} disabled={sammelGrund(v) !== null}
                      title={sammelGrund(v) ?? undefined} onChange={() => umschalten(v.sku)}
                    />
                    <div className="min-w-0">
                    {v.kennung ? (
                      <div className="max-w-[240px]"><ArtikelKennung kennung={v.kennung} sku={v.sku} name={v.name} /></div>
                    ) : (
                      // Ältere Antwort ohne Kennung: bisherige Darstellung.
                      <>
                        <div className="font-mono text-sm font-semibold text-ink">{v.sku}</div>
                        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-ink-2">
                          {v.merchant_sku && <GrundartikelLink code={v.merchant_sku} className="font-mono" />}
                          {v.set_menge !== null && v.set_menge > 1 && <span className="rounded bg-panel-2 px-1 font-mono">Set ×{v.set_menge}</span>}
                          {v.is_grundartikel && <span className="rounded bg-primary-soft px-1 text-primary">Grundartikel</span>}
                          {!v.is_grundartikel && v.sku !== fam.sku && (
                            <span className="font-mono text-muted" title="Variante gehört zu diesem Grundartikel">zu {fam.sku}</span>
                          )}
                        </div>
                        <div className="max-w-[220px] truncate text-xs text-muted" title={v.name}>{v.name}</div>
                      </>
                    )}
                    <Link
                      to={`/preise/rollout?tab=historie&sku=${encodeURIComponent(v.sku)}`}
                      className="mt-1 inline-block text-[11px] text-primary hover:underline"
                    >
                      Historie dieses Artikels
                    </Link>
                    <AngebotsLinks v={v} />
                    </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <StrategieWahl v={v} ziel={stratZiel} disabled={strategie.isPending} onWahl={(neu) => setStratOffen({ v, neu, ziel: stratZiel })} />
                    <AusnahmeHinweise v={v} ziel={stratZiel} />
                  </td>
                  {ansicht.portale.map((p) => (
                    <td key={p} className="px-3 py-3">
                      <PortalZelle
                        v={v} portal={p} mitRollout={p !== 'amazon'} gekoppelt={p === 'ebay' && kopplungsPaar(v) !== null} busy={busy}
                        onPause={(id) => pausieren.mutate(id)}
                        onFortsetzen={(portal, r) => oeffneFortsetzen(v, portal, r, v.portale[p].regel)}
                      />
                    </td>
                  ))}
                  <td className="px-3 py-3 pr-5">
                    <div className="flex min-w-[190px] flex-col gap-2.5">
                      {/* Amazon-Start nur bei aktivem Amazon-Angebot und Amazon im Portalfilter (Befund *406:
                          Klick bei Filter Shopify, Variante ohne Amazon-Angebot). Bestehende Amazon-Rollouts
                          solcher Varianten bleiben auf der Seite Preis-Rollout sichtbar. */}
                      {ansicht.portale.includes('amazon') && v.portale.amazon.angebot.zustand === 'aktiv' && (
                        <StartZeile
                          titel="Amazon" start={v.start.amazon} amZiel={startAmZiel(v, 'amazon')} kill={fam.kill_aktiv}
                          onStart={() => oeffneStart(v, 'amazon')} gesperrt={startSperre !== null}
                          halten={halteZeile(v, 'amazon')}
                          rollout={v.portale.amazon.rollout && (
                            <RolloutKnopf
                              rollout={v.portale.amazon.rollout} portal="amazon" wirkung={v.portale.amazon.wirkung} busy={busy}
                              onPause={(id) => pausieren.mutate(id)}
                              onFortsetzen={() => oeffneFortsetzen(v, 'amazon', v.portale.amazon.rollout!, v.portale.amazon.regel)}
                            />
                          )}
                        />
                      )}
                      <StartZeile
                        titel={v.start.base.fanout_portal ? `Basis → ${portalName(v.start.base.fanout_portal)}` : 'Basis'}
                        hinweis={v.start.base.fanout_portal === 'ebay' && !kopplungsPaar(v) ? 'startet Basis und eBay gekoppelt – ein Takt' : null}
                        start={v.start.base} amZiel={startAmZiel(v, 'base')} kill={fam.kill_aktiv}
                        onStart={() => oeffneStart(v, 'base')} gesperrt={startSperre !== null}
                        halten={halteZeile(v, 'base')}
                        rollout={(() => {
                          const sc = v.start.base.scopes.find((s) => s.portal === 'base')
                          const paar = kopplungsPaar(v)
                          if (paar && sc) {
                            return (
                              <PaarKnopf
                                paar={paar} wirkung={{ basis: sc.wirkung, ebay: v.portale.ebay.wirkung }} busy={busy}
                                onPause={(id) => pausieren.mutate(id)}
                                onFortsetzen={(id) => oeffneFortsetzen(
                                  v, 'Basis + eBay', id === paar.ebay.id ? paar.ebay : paar.basis,
                                  id === paar.ebay.id ? v.portale.ebay.regel : sc.regel,
                                )}
                              />
                            )
                          }
                          return sc?.bestehend ? (
                            <RolloutKnopf
                              rollout={sc.bestehend} portal="base" wirkung={sc.wirkung} busy={busy}
                              onPause={(id) => pausieren.mutate(id)}
                              onFortsetzen={() => oeffneFortsetzen(v, 'base', sc.bestehend!, sc.regel)}
                            />
                          ) : null
                        })()}
                      />
                    </div>
                  </td>
                </tr>
                {verlaufZeilen(v).length > 0 && (
                  <tr data-testid="verlauf-zeile">
                    <td colSpan={3 + ansicht.portale.length} className="px-5 pb-3 pt-0">
                      <div className="flex flex-col gap-1 border-l-2 border-line-2 pl-3">
                        {verlaufZeilen(v).map((z) => (
                          <RolloutVerlauf
                            key={z.id} titel={z.titel} kennung={v.kennung}
                            verlauf={verlauf.data?.[String(z.id)]} laedt={verlauf.isLoading} fehler={verlauf.isError}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              ))}
              </Fragment>
            ))}
          </table>
        </div>
      )}

      <StartDialog
        offen={startWahl ? startAktuell : null}
        wahlOffen={startWahl !== null}
        liveWrites={fam?.live_writes ?? false}
        amazonGateOffen={fam?.amazon_fee_gate_offen ?? false}
        busy={starten.isPending}
        sperre={startSperre}
        veraltet={startVeraltet}
        onUebernehmen={() => startWahl && startAktuell && setStartWahl({ ...startWahl, bestaetigt: startStand(startAktuell.start) })}
        fehler={startFehler}
        onAbbrechen={() => setStartWahl(null)}
        onStarten={bestaetigeStart}
      />
      <SammelstartDialog
        auswahl={sammel}
        liveWrites={fam?.live_writes ?? false}
        sperre={startSperre}
        ladeFamilie={ladeFamilieFrisch}
        starte={createRollout}
        onSchliessen={(gelaufen) => {
          setSammel(null)
          if (gelaufen) { setAuswahl(new Set()); neuLaden() }
        }}
      />
      <StrategieDialog
        offen={stratOffen}
        busy={strategie.isPending}
        onAbbrechen={() => setStratOffen(null)}
        onBestaetigen={() => { if (stratOffen && !strategie.isPending) strategie.mutate(stratOffen) }}
      />
      <FortsetzenDialog
        offen={fortOffen}
        busy={fortsetzen.isPending}
        fehler={fortFehler}
        onAbbrechen={() => setFortOffen(null)}
        onBestaetigen={() => { if (fortOffen && !fortsetzen.isPending) { setFortFehler(null); fortsetzen.mutate(fortOffen.id) } }}
      />
    </Panel>
  )
}

/** Rollouts einer Variante für den Verlauf: Paar Basis + eBay als eine Zeile, sonst je Portal. */
function verlaufZeilen(v: FamilyVariante): { id: number; titel: string }[] {
  const out: { id: number; titel: string }[] = []
  const paar = kopplungsPaar(v)
  const basis = v.start.base.scopes.find((s) => s.portal === 'base')?.bestehend ?? null
  if (v.portale.amazon.rollout) out.push({ id: v.portale.amazon.rollout.id, titel: 'Amazon' })
  if (paar) out.push({ id: paar.basis.id, titel: 'Basis + eBay' })
  else {
    if (basis) out.push({ id: basis.id, titel: 'Basis' })
    if (v.portale.ebay.rollout) out.push({ id: v.portale.ebay.rollout.id, titel: 'eBay' })
  }
  if (v.portale.shopify.rollout) out.push({ id: v.portale.shopify.rollout.id, titel: 'Shopify' })
  if (v.portale.metro.rollout) out.push({ id: v.portale.metro.rollout.id, titel: 'Metro' })
  if (v.portale.kaufland?.rollout) out.push({ id: v.portale.kaufland.rollout.id, titel: 'Kaufland' })
  return out
}

const STATUS_OPTIONEN: { wert: StatusFilter; text: string }[] = [
  { wert: 'aktiv', text: 'Aktiv' }, { wert: 'nicht_aktiv', text: 'Nicht aktiv' }, { wert: 'alle', text: 'Alle' },
]

function Werkzeugleiste({ ansicht, setAnsicht, zaehler }: {
  ansicht: RolloutAnsicht
  setAnsicht: (a: Partial<RolloutAnsicht>) => void
  zaehler: string | null
}) {
  const togglePortal = (p: SichtPortal) =>
    setAnsicht({ portale: ansicht.portale.includes(p) ? ansicht.portale.filter((x) => x !== p) : [...ansicht.portale, p] })
  const auswahl = 'h-8 rounded-md border border-line bg-bg px-2 text-xs text-ink focus-visible:outline-2 focus-visible:outline-primary'
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-line-2 px-5 py-3">
      <fieldset className="flex items-center">
        <legend className="sr-only">Angebot</legend>
        <div className="inline-flex rounded-md border border-line bg-panel-2 p-0.5">
          {STATUS_OPTIONEN.map((o) => (
            <label
              key={o.wert}
              className={cn(
                'cursor-pointer rounded px-2.5 py-1 text-xs transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary',
                ansicht.status === o.wert ? 'bg-panel font-semibold text-ink shadow-xs' : 'text-ink-2 hover:text-ink',
              )}
            >
              <input
                type="radio" name="ro-status" value={o.wert} className="sr-only"
                checked={ansicht.status === o.wert} onChange={() => setAnsicht({ status: o.wert })}
              />
              {o.text}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex items-center gap-1.5">
        <legend className="sr-only">Portale</legend>
        {SICHT_PORTALE.map((p) => {
          const an = ansicht.portale.includes(p)
          return (
            <label
              key={p}
              className={cn(
                'inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary',
                an ? 'border-line bg-panel text-ink' : 'border-dashed border-line text-muted hover:text-ink-2',
              )}
            >
              <input type="checkbox" className="sr-only" checked={an} onChange={() => togglePortal(p)} />
              <span
                aria-hidden
                className="inline-block size-2.5 rounded-sm border"
                style={{ background: an ? PORTAL_FARBE[p] : 'transparent', borderColor: PORTAL_FARBE[p] }}
              />
              {portalName(p)}
            </label>
          )
        })}
      </fieldset>

      <label className="flex items-center gap-1.5 text-xs text-ink-2">
        <span className="sr-only">Strategie</span>
        <select
          aria-label="Strategie" className={auswahl} value={ansicht.strategie}
          onChange={(e) => setAnsicht({ strategie: e.target.value as StrategieFilter })}
        >
          <option value="alle">Alle Strategien</option>
          <option value="margin">Marge</option>
          <option value="break_even">Break-Even</option>
          <option value="uneinheitlich">Uneinheitlich</option>
        </select>
      </label>

      <label className="flex items-center gap-1.5 text-xs text-ink-2">
        <span className="sr-only">Rollout</span>
        <select
          aria-label="Rollout" className={auswahl} value={ansicht.rollout}
          onChange={(e) => setAnsicht({ rollout: e.target.value as RolloutFilter })}
          title="Ausgerollt: jedes gezeigte Portal mit aktivem Angebot steht am Ziel, hat einen laufenden Rollout oder einen gehaltenen Ist-Preis."
        >
          <option value="alle">Alle Rollout-Stände</option>
          <option value="offen">Rollout offen</option>
          <option value="ausgerollt">Ausgerollt (am Ziel, läuft, gehalten)</option>
        </select>
      </label>

      <label className="flex items-center gap-1.5 text-xs text-ink-2">
        <span aria-hidden>Sortierung</span>
        <select
          aria-label="Sortierung" className={auswahl} value={ansicht.sort}
          onChange={(e) => setAnsicht({ sort: e.target.value as Sortierung })}
        >
          <option value="angebot">Angebot (gleiche Auktion zusammen)</option>
          <option value="variante">Variante</option>
          <option value="strategie">Strategie</option>
          <option value="abstand">Abstand Ist → Ziel</option>
        </select>
      </label>

      {zaehler && <span className="ml-auto text-xs text-muted">{zaehler}</span>}
    </div>
  )
}

function StrategieWahl({ v, ziel, disabled, onWahl }: {
  v: FamilyVariante; ziel: 'amazon' | 'base' | 'both'; disabled: boolean; onWahl: (s: PricingStrategy) => void
}) {
  // Nur der gewählte Kanal zählt: bei einem einzelnen Kanal gibt es kein „gemischt“.
  const un = ziel === 'both' && v.strategie.uneinheitlich
  const wert = ziel === 'both' ? v.strategie.amazon : v.strategie[ziel]
  return (
    <div className="flex min-w-[120px] flex-col items-start gap-1">
      <select
        aria-label={`Strategie ${v.sku}`}
        value={un ? '' : wert}
        disabled={disabled}
        onChange={(e) => { if (e.target.value) onWahl(e.target.value as PricingStrategy) }}
        className="h-7 rounded border border-line bg-bg px-1 text-xs text-ink focus-visible:outline-2 focus-visible:outline-primary"
      >
        {un && <option value="" disabled>gemischt</option>}
        <option value="margin">Marge</option>
        <option value="break_even">Break-Even</option>
      </select>
      {un && (
        <span
          className="rounded bg-warn-soft px-1.5 py-px ring-1 ring-warn/60 text-[10px] font-semibold text-ink"
          title={`Amazon: ${v.strategie.amazon === 'margin' ? 'Marge' : 'Break-Even'} · Basis: ${v.strategie.base === 'margin' ? 'Marge' : 'Break-Even'}`}
        >
          uneinheitlich
        </span>
      )}
      {(v.spuren.amazon.warnung || v.spuren.base.warnung) && (
        <span className="text-[10px] text-ink-2">Break-Even wirkt nur bei inkl. Versand</span>
      )}
    </div>
  )
}

/** „! 7,99 € vom Nutzer übernommen“ unter der Strategie — je Spur, die der Portalfilter zeigt. */
function AusnahmeHinweise({ v, ziel }: { v: FamilyVariante; ziel: 'amazon' | 'base' | 'both' }) {
  const spuren = (['amazon', 'base'] as const).filter((s) => (ziel === 'both' || ziel === s) && v.spuren[s].ausnahme)
  if (spuren.length === 0) return null
  return (
    <div className="mt-1.5 flex flex-col items-start gap-1">
      {spuren.map((s) => {
        const a = v.spuren[s].ausnahme!
        const name = s === 'amazon' ? 'Amazon' : 'Basis'
        return (
          <span
            key={s} data-testid={`ausnahme-${s}`}
            className="inline-flex items-baseline gap-1 rounded bg-warn-soft px-1.5 py-px text-[10px] text-ink ring-1 ring-warn/60"
            title={`${name}: Ist-Preis ${fmtEUR(a.preis)} statt Ziel ${fmtEUR(a.ziel_bei_anlage)} gehalten${a.von ? ` (${a.von})` : ''}. Endet von selbst bei Strategiewechsel oder neuem Zielpreis.`}
          >
            <span aria-hidden className="font-bold text-warn">!</span>
            <span className="font-mono font-semibold">{fmtEUR(a.preis)}</span>
            <span>vom Nutzer übernommen{ziel === 'both' ? ` · ${name}` : ''}</span>
          </span>
        )
      })}
    </div>
  )
}

/** Häkchen „Ist-Preis halten“: hält den aktuellen Ist-Preis statt des Ziels; Abhaken hebt die Ausnahme auf. */
function IstHalten({ sku, spur, ausnahme, istPreis, moeglich, disabled, onWechsel }: {
  sku: string; spur: 'amazon' | 'base'; ausnahme: SpurAusnahme | null; istPreis: number | null
  moeglich: boolean; disabled: boolean; onWechsel: (an: boolean) => void
}) {
  if (!ausnahme && !moeglich) return null
  const preis = ausnahme?.preis ?? istPreis
  return (
    <label
      className={cn(
        'inline-flex items-center gap-1.5 text-[11px] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary',
        ausnahme ? 'font-semibold text-ink' : 'text-ink-2 hover:text-ink',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
      title={ausnahme
        ? 'Haken entfernen hebt die Ausnahme auf; danach ist ein normaler Rollout-Start wieder möglich.'
        : 'Hält den aktuellen Ist-Preis statt des Ziels. Laufende Rollouts dieser Spur werden pausiert; Start und Sammelstart lassen die Variante aus – bis Strategie oder Zielpreis sich ändern.'}
    >
      <input
        type="checkbox" className="accent-warn"
        aria-label={`${spur === 'amazon' ? 'Amazon' : 'Basis'}: Ist-Preis halten ${sku}`}
        checked={ausnahme !== null} disabled={disabled} onChange={(e) => onWechsel(e.target.checked)}
      />
      Ist-Preis{preis !== null ? ` ${fmtEUR(preis)}` : ''} halten
    </label>
  )
}

function StartZeile({ titel, hinweis = null, start, amZiel, kill, onStart, gesperrt, rollout, halten = null }: {
  titel: string
  /** Kurzer Kopftext unter dem Titel (z. B. Kopplung Basis → eBay). */
  hinweis?: string | null
  start: FamilyStart
  /** Jeder Scope steht schon auf dem Ziel: ein Start ändert keinen Preis — kein Knopf. */
  amZiel: boolean
  kill: boolean
  onStart: () => void
  /** Daten laden neu, sind nach einer Änderung nicht frisch oder fehlerhaft: kein Start. */
  gesperrt: boolean
  rollout: ReactNode
  /** Häkchen „Ist-Preis halten“ (Plan 2026-10-30). */
  halten?: ReactNode
}) {
  const grund = grundText(start)
  const gehalten = start.grund === 'ausnahme' && !kill
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold text-ink">{titel}</span>
        {start.startbar && !kill && amZiel ? (
          <span className="text-right text-[11px] font-medium text-success" title="Ist-Preis = Zielpreis – ein Start würde nichts ändern">
            Preis am Ziel
          </span>
        ) : start.startbar && !kill ? (
          <Button
            size="sm" variant="outline" className="h-6 px-2 text-[11px]"
            aria-label={`${start.portal === 'amazon' ? 'Amazon' : 'Basis'}: Rollout starten`}
            onClick={onStart} disabled={gesperrt}
          >
            Rollout starten
          </Button>
        ) : gehalten ? (
          <span className="text-right text-[11px] font-semibold text-warn" title="Ist-Preis vom Nutzer übernommen – Start erst nach Aufheben der Ausnahme">
            {grund}
          </span>
        ) : (
          <span className="max-w-[150px] text-right text-[11px] font-medium text-ink-2" title={start.server_grund ?? start.grund ?? undefined}>
            {grund ?? 'Not-Aus aktiv'}
          </span>
        )}
      </div>
      {hinweis && <div className="text-[10px] text-muted">{hinweis}</div>}
      {halten}
      {rollout}
    </div>
  )
}
